import React, { useEffect, useState } from "react";
import {
  Container,
  Button,
  AppBar,
  Toolbar,
  Paper,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

class PDFImageManager {
  constructor() {
    this.imageCache = new Map();
    this.maxCacheSize = 50;
  }

  isBase64String(str) {
    const base64Patterns = [
      "/9j/",
      "iVBORw0KGgoAAAANSUhEUgAA",
      "R0lGODlh",
      "UklGR",
      "Qk0",
    ];
    return base64Patterns.some((pattern) => str && str.startsWith(pattern));
  }

  formatBase64ForPDF(base64String) {
    if (base64String.startsWith("data:image/")) return base64String;
    let mimeType = "image/jpeg";
    if (base64String.startsWith("iVBORw0KGgoAAAANSUhEUgAA"))
      mimeType = "image/png";
    else if (base64String.startsWith("R0lGODlh")) mimeType = "image/gif";
    else if (base64String.startsWith("UklGR")) mimeType = "image/webp";
    return `data:${mimeType};base64,${base64String}`;
  }

  async loadImageFromSource(source) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`Timeout for ${source.name}`);
      controller.abort();
    }, 8000);
    try {
      console.log(`Fetching image from ${source.name}: ${source.url}`);
      const response = await fetch(source.url, {
        signal: controller.signal,
        headers: {
          Accept: "image/*",
          "User-Agent":
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
        },
        mode: "cors",
      });
      clearTimeout(timeoutId);
      console.log(`Response status for ${source.name}: ${response.status}`);
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const blob = await response.blob();
      console.log(`Blob type: ${blob.type}, size: ${blob.size}`);
      if (!blob.type.startsWith("image/"))
        throw new Error(`Invalid content type: ${blob.type}`);
      return await this.blobToBase64(blob);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`Error loading image from ${source.name}:`, error.message);
      throw error;
    }
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  getCachedImage(imagePath) {
    return this.imageCache.get(imagePath);
  }

  cacheImage(imagePath, imageData) {
    if (this.imageCache.size >= this.maxCacheSize) {
      const firstKey = this.imageCache.keys().next().value;
      this.imageCache.delete(firstKey);
    }
    this.imageCache.set(imagePath, imageData);
  }

  getLogoSources(logoUrl) {
    const baseUrl = "https://arizshad-002-site5.ktempurl.com/";
    const fullUrl = logoUrl.startsWith("http")
      ? logoUrl
      : `${baseUrl}${logoUrl.replace(/^[~\/]+/, "")}`;
    return [
      { name: "direct", url: fullUrl, method: "direct" },
      {
        name: "cors-proxy-allorigins",
        url: `https://api.allorigins.win/raw?url=${encodeURIComponent(
          fullUrl
        )}`,
        method: "proxy",
      },
      {
        name: "cors-proxy-corsproxy",
        url: `https://corsproxy.io/?${encodeURIComponent(fullUrl)}`,
        method: "proxy",
      },
    ];
  }

  getImageSources(imagePath) {
    const cleanPath = imagePath.replace("~", "");
    const baseUrl = "https://arizshad-002-site5.ktempurl.com/";
    return [
      { name: "original", url: `${baseUrl}${cleanPath}`, method: "direct" },
      {
        name: "cors-proxy",
        url: `https://api.allorigins.win/raw?url=${encodeURIComponent(
          `${baseUrl}${cleanPath}`
        )}`,
        method: "proxy",
      },
    ];
  }

  async handleLogoImage() {
    console.log("=== LOGO DEBUGGING START ===");
    const logoUrl = sessionStorage
      .getItem("logoImg")
      ?.replace(/['"]+/g, "")
      .trim();
    console.log("Raw logo URL from session:", logoUrl);
    if (!logoUrl || logoUrl === "N/A" || logoUrl === "") {
      console.log("Invalid or missing logo URL, skipping logo");
      return null;
    }
    if (logoUrl.startsWith("data:image/")) {
      console.log("Using direct base64 logo");
      return { type: "base64", data: logoUrl };
    }
    if (this.isBase64String(logoUrl)) {
      console.log("Formatting raw base64 logo");
      return { type: "base64", data: this.formatBase64ForPDF(logoUrl) };
    }
    const cacheKey = `logo_${logoUrl}`;
    const cachedLogo = this.getCachedImage(cacheKey);
    if (cachedLogo) {
      console.log("Using cached logo");
      return { type: "base64", data: cachedLogo };
    }
    const logoSources = this.getLogoSources(logoUrl);
    for (const source of logoSources) {
      try {
        console.log(`Trying logo source: ${source.name}`);
        const logoData = await this.loadImageFromSource(source);
        if (logoData) {
          console.log(`Successfully loaded logo from ${source.name}`);
          this.cacheImage(cacheKey, logoData);
          return { type: "base64", data: logoData };
        }
      } catch (error) {
        console.warn(`Failed to load logo from ${source.name}:`, error.message);
      }
    }
    console.log("All logo sources failed");
    console.log("=== LOGO DEBUGGING END ===");
    return null;
  }

  async handleStudentImage(studentData) {
    const imagePath = studentData.studentProfileLogo;
    console.log(
      `Processing student image for ${studentData.studentName}: ${imagePath}`
    );
    if (!imagePath || imagePath === "N/A" || imagePath === "") {
      console.log("No valid student image provided, using default avatar");
      return this.createDefaultAvatar();
    }
    // If already base64 or data URL, use directly
    if (imagePath.startsWith("data:image/")) {
      console.log("Student image is a data URL, using directly");
      return { type: "base64", data: imagePath };
    }
    if (this.isBase64String(imagePath)) {
      console.log("Student image is base64, formatting for PDF");
      return { type: "base64", data: this.formatBase64ForPDF(imagePath) };
    }
    const cachedImage = this.getCachedImage(imagePath);
    if (cachedImage) {
      console.log("Using cached student image");
      return { type: "base64", data: cachedImage };
    }
    const imageSources = this.getImageSources(imagePath);
    for (const source of imageSources) {
      try {
        console.log(`Trying student image source: ${source.name}`);
        const imageData = await this.loadImageFromSource(source);
        if (imageData) {
          console.log(`Successfully loaded student image from ${source.name}`);
          this.cacheImage(imagePath, imageData);
          return { type: "base64", data: imageData };
        }
      } catch (error) {
        console.warn(
          `Failed to load student image from ${source.name}:`,
          error.message
        );
      }
    }
    console.log("All student image sources failed, using default avatar");
    return this.createDefaultAvatar();
  }

  createDefaultAvatar() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 100;
    canvas.height = 100;
    ctx.fillStyle = "#e0e0e0";
    ctx.beginPath();
    ctx.arc(50, 50, 50, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillStyle = "#999";
    ctx.beginPath();
    ctx.arc(50, 35, 15, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(50, 70, 20, 0, Math.PI);
    ctx.fill();
    return { type: "canvas", data: canvas.toDataURL("image/png", 0.8) };
  }

  async addLogoToPDF(doc, logoResult, x, y, width, height) {
    try {
      if (logoResult?.type === "base64") {
        const testImg = new Image();
        testImg.src = logoResult.data;
        await new Promise((resolve, reject) => {
          testImg.onload = () => resolve();
          testImg.onerror = () => reject(new Error("Invalid logo image data"));
        });
        try {
          doc.addImage(logoResult.data, "JPEG", x, y, width, height);
          console.log("Logo added as JPEG");
        } catch (jpegError) {
          console.warn("JPEG failed, trying PNG:", jpegError.message);
          doc.addImage(logoResult.data, "PNG", x, y, width, height);
          console.log("Logo added as PNG");
        }
      }
    } catch (error) {
      console.warn("Failed to add logo:", error.message);
    }
  }

  addImageToPDF(doc, imageResult, x, y, width, height) {
    try {
      if (imageResult.type === "base64" || imageResult.type === "canvas") {
        // Try JPEG first, fallback to PNG if error
        try {
          doc.addImage(imageResult.data, "JPEG", x, y, width, height);
          console.log("Student image added as JPEG");
        } catch (jpegError) {
          console.warn("JPEG failed, trying PNG:", jpegError.message);
          doc.addImage(imageResult.data, "PNG", x, y, width, height);
          console.log("Student image added as PNG");
        }
      } else {
        this.addPlaceholderToPDF(doc, x, y, width, height);
      }
    } catch (error) {
      console.warn("Failed to add student image:", error.message);
      this.addPlaceholderToPDF(doc, x, y, width, height);
    }
  }

  addPlaceholderToPDF(doc, x, y, width, height) {
    doc.setFillColor(240, 240, 240);
    doc.rect(x, y, width, height, "F");
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(x, y, width, height);
    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.text("Photo", x + width / 2, y + height / 2, { align: "center" });
  }

  async buildPDFStructure(doc, studentData, xOffset, yOffset) {
    const pageWidth = 85;
    const pageHeight = 115;
    const logoX = xOffset + 3;
    const logoY = yOffset + 2;
    const logoWidth = 13;
    const logoHeight = 13;
    const headerHeight = 20;
    const verticalTop = yOffset + 8;

    // Calculate text area
    const gapAfterLogo = 18;
    const textXStart = logoX + logoWidth + gapAfterLogo;
    const textMaxWidth = pageWidth - (logoX - xOffset) - logoWidth - gapAfterLogo - 2;
    const textCenterX = xOffset + pageWidth / 2;

    // Draw card background
    doc.setFillColor(255, 255, 255);
    doc.rect(xOffset, yOffset, pageWidth, pageHeight, "F");
    doc.setFillColor(0, 51, 102);
    doc.rect(xOffset, yOffset, pageWidth, headerHeight, "F");

    // Draw border with same thickness on all sides
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.rect(xOffset, yOffset, pageWidth, pageHeight);

    // Add logo
    const logoResult = await this.handleLogoImage();
    if (logoResult) {
      await this.addLogoToPDF(doc, logoResult, logoX, logoY, logoWidth, logoHeight);
    }

    // Render school name
    const schoolName = sessionStorage.getItem("organizationName")?.replace(/['"]+/g, "") || "N/A";
    console.log(`Rendering school name: ${schoolName}, maxWidth: ${textMaxWidth}`);
    doc.setFont("helvetica", "bold");
    let fontSize = schoolName.length > 30 ? 8 : 10;
    doc.setFontSize(fontSize);
    doc.setTextColor(255, 255, 255);
    const nameLines = doc.splitTextToSize(schoolName, textMaxWidth);
    console.log(`School name lines: ${nameLines.length}`, nameLines);
    let currentY = verticalTop;
    const maxLines = 2;
    nameLines.slice(0, maxLines).forEach((line, index) => {
      if (currentY + 4 <= yOffset + headerHeight - 2) {
        doc.text(line, textCenterX, currentY, { align: "center" });
        console.log(`Drawing line "${line}" at x: ${textCenterX}, y: ${currentY}`);
        currentY += 4;
      }
    });

    // Render session text
    const sessionText = (studentData.session || "Session 2024-2025") + " (Registered)";

    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    if (currentY + 4 <= yOffset + headerHeight) {
      doc.text(sessionText, textCenterX, currentY, { align: "center" });
      console.log(`Drawing session "${sessionText}" at x: ${textCenterX}, y: ${currentY}`);
    }

    // Draw content area rectangle
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.rect(xOffset + 2, yOffset + 23, pageWidth - 4, pageHeight - 43);

    // Add student photo
    const photoWidth = 25;
    const photoHeight = 25;
    const photoX = xOffset + (pageWidth - photoWidth) / 2;
    const photoY = yOffset + 27;
    const imageResult = await this.handleStudentImage(studentData);
    this.addImageToPDF(doc, imageResult, photoX, photoY, photoWidth, photoHeight);
  }


  addStudentContent(doc, studentData, xOffset, yOffset) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(studentData.studentName || "N/A", xOffset + 45, yOffset + 58, { align: "center" });

    // Adjust the box size and position with a top margin
    const boxYOffset = yOffset + 60;
    doc.setFillColor(245, 245, 245);
    doc.rect(xOffset + 7, boxYOffset, 70, 30, "F");
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(xOffset + 7, boxYOffset, 70, 30);

    const details = [
      `Class: ${studentData.class || "N/A"}`,
      `Admission No: ${studentData.admissionNo || "N/A"}`,
      `Phone: ${studentData.fatherMobile || "N/A"}`,
      `Father Name: ${studentData.guardianName || "N/A"}`,
      `Address: ${studentData.permanentAddress || "N/A"}`,
    ];

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    let yPos = boxYOffset + 7;

    details.forEach((detail) => {
      const wrappedText = doc.splitTextToSize(detail, 65);
      wrappedText.forEach((line) => {
        if (yPos < boxYOffset + 30) {
          doc.text(line, xOffset + 10, yPos);
          yPos += 4;
        }
      });
    });

    // Footer section
    doc.setFillColor(0, 51, 102);
    doc.rect(xOffset, yOffset + 100, 85, 15, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);

    const address = sessionStorage.getItem("address")?.replace(/['"]+/g, "") || "";
    const schoolPhoneNumber = sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, "") || "";

    console.log(`Rendering address for student ${studentData.studentName}: ${address}`);
    if (address) {
      const lines = doc.splitTextToSize(address, 70);
      const maxLines = 2;
      const displayLines = lines.slice(0, maxLines);
      let startY = yOffset + 105;
      displayLines.forEach((line, index) => {
        doc.text(line, xOffset + 42.5, startY + index * 4, { align: "center" });
      });
    }

    // Always show phone number under the address
    if (schoolPhoneNumber) {
      doc.text(`(Phone: ${schoolPhoneNumber})`, xOffset + 42.5, yOffset + 113, { align: "center" });
    }
  }




}
const BulkIdCard = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pdfManager = new PDFImageManager();

  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      console.log("Fetching classes from:", `${apiUrl}/Enquiry/ddlClassName`);
      console.log("Token:", token);
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      console.log("Classes response status:", response.status);
      if (!response.ok)
        throw new Error(
          `Failed to fetch classes: ${response.status} ${response.statusText}`
        );
      const data = await response.json();
      console.log("Classes data:", data);
      setClasses(data);
    } catch (error) {
      console.error("Error fetching classes:", error.message);
      // alert(`Failed to fetch classes: ${error.message}`);
      setSnackbarMessage(`Failed to fetch classes: ${error.message}`);
      setOpenSnackbar(true);
      setSnackbarSeverity("error")

    }
  };

  const fetchSections = async (classId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      console.log("Fetching sections for classId:", classId);
      const response = await fetch(
        `${apiUrl}/ClassPromotion/ddlSection_clsId`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ teacherId: 0, classId }),
        }
      );
      console.log("Sections response status:", response.status);
      if (!response.ok)
        throw new Error(
          `Failed to fetch sections: ${response.status} ${response.statusText}`
        );
      const data = await response.json();
      console.log("Sections data:", data);
      setSections(data);
    } catch (error) {
      console.error("Error fetching sections:", error.message);
      // alert(`Failed to fetch sections: ${error.message}`);
      setSnackbarMessage(`Failed to fetch sections: ${error.message}`);
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }
  };

  const fetchStudents = async () => {
    if (!selectedClass || !selectedSection) return;
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      console.log("Fetching students with:", {
        classId: selectedClass,
        sectionId: selectedSection,
      });
      const response = await fetch(`${apiUrl}/Admission/GetIdCards`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          classId: selectedClass,
          sectionId: selectedSection,
        }),
      });
      console.log("Students response status:", response.status);
      if (!response.ok)
        throw new Error(
          `Failed to fetch students: ${response.status} ${response.statusText}`
        );
      const data = await response.json();
      console.log("Students data:", data);
      if (data.length === 0) {
        // alert("No students found for selected class and section");
        setSnackbarMessage("No students found for selected class and section");
        setOpenSnackbar(true);
        setSnackbarSeverity("error")

      }
      setStudents(data);
    } catch (error) {
      console.error("Error fetching students:", error.message);
      // alert(`Failed to fetch student data: ${error.message}`);
       setSnackbarMessage(`Failed to fetch student data: ${error.message}`);
        setOpenSnackbar(true);
        setSnackbarSeverity("error")

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      setSections([]);
      setSelectedSection("");
      fetchSections(selectedClass);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSection) {
      fetchStudents();
    }
  }, [selectedSection]);

  const generateBulkIdCardPDF = async () => {
    if (!students.length) {
      // alert("No students selected to generate ID cards");
       setSnackbarMessage("No students selected to generate ID cards");
        setOpenSnackbar(true);
        setSnackbarSeverity("error")

      return;
    }

    setLoading(true);
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // A4 landscape dimensions: 297mm x 210mm
      const pageWidth = 297;
      const pageHeight = 210;
      const cardWidth = 85; // Reduced from 90 to fit within page
      const cardHeight = 115;
      const marginX = 10; // Reduced margin for better fit
      const marginY = 10;
      const cardSpacingX = 10; // Reduced spacing between cards
      const cardSpacingY = 10;
      const cardsPerRow = 3;
      const cardsPerCol = 1; // Can be increased to 2 if vertical space allows

      // Calculate offsets
      const xOffsets = [
        marginX,
        marginX + cardWidth + cardSpacingX,
        marginX + 2 * (cardWidth + cardSpacingX),
      ];
      const yOffsets = [marginY];

      // Verify layout fits within page
      const totalWidth = xOffsets[2] + cardWidth;
      if (totalWidth > pageWidth - marginX) {
        console.error(
          `Total width (${totalWidth}mm) exceeds page width (${pageWidth - marginX
          }mm)`
        );
        // alert("Card layout exceeds page width. Please contact support.");
         setSnackbarMessage("Card layout exceeds page width. Please contact support.");
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
        
        return;
      }

      let cardIndex = 0;
      for (let i = 0; i < students.length; i++) {
        const student = students[i];
        const col = cardIndex % cardsPerRow;
        const row = Math.floor(cardIndex / cardsPerRow) % cardsPerCol;
        const xOffset = xOffsets[col];
        const yOffset = yOffsets[row];

        console.log(
          `Generating card for student: ${student.studentName} at x: ${xOffset}, y: ${yOffset}`
        );

        // Build card structure
        await pdfManager.buildPDFStructure(doc, student, xOffset, yOffset);

        // Add student image
        const imageResult = await pdfManager.handleStudentImage(student);
        await pdfManager.addImageToPDF(
          doc,
          imageResult,
          xOffset + 30,
          yOffset + 27,
          25,
          25
        );

        // Add student content
        pdfManager.addStudentContent(doc, student, xOffset, yOffset);

        cardIndex++;

        // Add new page if needed
        if (cardIndex === cardsPerRow * cardsPerCol && i < students.length - 1) {
          doc.addPage();
          cardIndex = 0;
        }
      }

      const pdfBlob = doc.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
    } catch (error) {
      console.error("Error generating bulk ID card PDF:", error.message);
      // alert(`Failed to generate bulk ID card PDF: ${error.message}`);
       setSnackbarMessage(`Failed to generate bulk ID card PDF: ${error.message}`);
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
    } finally {
      setLoading(false);
    }
  };


  return (
    <Container maxWidth="md" >
      <AppBar position="static" sx={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Bulk ID Card Generation
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: '20px', marginTop: '6px', textAlign: 'left' }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 2 }}>
          <FormControl fullWidth variant="outlined" sx={{ maxWidth: '100%' }}>
            <InputLabel shrink={!!selectedClass}>Class</InputLabel>
            <Select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              label="Class"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Select Class</em>
              </MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.classId} value={cls.classId}>
                  {cls.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" sx={{ maxWidth: '100%' }}>
            <InputLabel shrink={!!selectedSection}>Section</InputLabel>
            <Select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              label="Section"
              disabled={loading || !selectedClass}
            >
              <MenuItem value="">
                <em>Select Section</em>
              </MenuItem>
              {sections.map((section) => (
                <MenuItem key={section.sectionId} value={section.sectionId}>
                  {section.sectionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" justifyContent="center" sx={{ mt: 2, gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{

              color: "#ffffff",
              width: "180px",
              height: "50px",
              padding: "10px 0",
              fontWeight: 'bold',

            }}
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Back to Admission
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4caf50",
              color: "#ffffff",
              width: "210px",
              height: "50px",
              padding: "10px 0",
              fontWeight: 'bold',
              "&:hover": { backgroundColor: "#388e3c" },
            }}
            onClick={generateBulkIdCardPDF}
            disabled={loading || !students.length}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Generating..." : "Generate Bulk ID Cards"}
          </Button>

        </Box>
      </Paper>

    </Container>
  );
};

export default BulkIdCard;
